import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as _ from "lodash";
import { EditActions, editActions } from "../../actions/editActions";
import { nameofFactory, hasWriteAccess } from "../../utils/utils";
import {
  getDomainGroupNames,
  parameterCategoryHasChildren,
  getDomainOptionsByDomainGroupId
} from "../../utils/pdApiUtils";
import Editor from "../../components/Editing/Editor";
import NumberField from "../../components/Editing/NumberField";
import TextField from "../../components/Editing/TextField";
import DropdownField from "../../components/Editing/DropdownField";

type StateProps = {
  editedParameterCategory: Api.ParameterCategory;
  storedParameterCategory: Api.ParameterCategory;
  pdApi: State.PdApi;
  activeUser?: Api.User;
};

type DispatchProps = {
  editActions: typeof editActions;
};

type OwnProps = {
  domainGroupId?: number;
  groupName?: Api.GroupName;
  selectedParentId?: number;
  toggleModal: () => void;
};

interface ErrorStatus {
  errorStatus: boolean;
  errorMsg: string;
}

const parameterCategoryFields = nameofFactory<Api.ParameterCategory>();

export type EditParameterCategoryProps = StateProps & DispatchProps & OwnProps;

export class EditParameterCategory extends React.Component<EditParameterCategoryProps> {
  render(): JSX.Element {
    const {
      editedParameterCategory,
      pdApi,
      storedParameterCategory,
      activeUser,
      domainGroupId,
      selectedParentId,
      toggleModal
    } = this.props;
    const { parameterCategories } = pdApi;
    const domainOptions = getDomainGroupNames(pdApi);
    const isNew = !storedParameterCategory;
    const editorTitle: string = this.buildEditorTitle(isNew);
    let parentCategoryName = "";
    let filteredDomainOptions;
    let parentCategoryDomainGroupId: number | undefined = undefined;
    let accessdisable = true;
    // If Parent has been selected, add parentId and domainGroup to edited object and display in form
    if (selectedParentId && selectedParentId !== editedParameterCategory.id) {
      editedParameterCategory.parentCategory = { id: selectedParentId, url: "" };
      const parentCategory = _.values(this.props.pdApi.parameterCategories).filter(
        row => row.id === selectedParentId
      )[0];
      parentCategoryName = parentCategory.enumName;
      parentCategoryDomainGroupId = parentCategory.domainGroup.id;
    }

    if (activeUser && activeUser.roles.some(role => role.name === "admin")) {
      filteredDomainOptions = domainOptions;
    } else {
      filteredDomainOptions = domainOptions.filter(domainOption => {
        const groupName: Api.GroupName = domainOption.text as Api.GroupName;
        return hasWriteAccess(activeUser, groupName);
      });
      if (editedParameterCategory.domainGroup.id === 1) {
        const defaultDomainGroupId = filteredDomainOptions[0].value;
        const domainGroup = pdApi.domainGroups[defaultDomainGroupId];
        this.props.editActions.setEdited({ ...editedParameterCategory, domainGroup });
      }
      // Compute accessdisable from the parent's domainGroup without mutating Redux state
      if (parentCategoryDomainGroupId !== undefined) {
        const currentDomainGroup = pdApi.domainGroups[parentCategoryDomainGroupId];
        accessdisable = currentDomainGroup
          ? currentDomainGroup.id === parentCategoryDomainGroupId
          : false;
        if (editedParameterCategory.domainGroup.id !== parentCategoryDomainGroupId) {
          this.props.editActions.setEdited({
            ...editedParameterCategory,
            domainGroup: currentDomainGroup
          });
        }
      }
    }

    if (domainGroupId) {
      editedParameterCategory.domainGroup.id = domainGroupId;
    } else if (parentCategoryDomainGroupId) {
      editedParameterCategory.domainGroup.id = parentCategoryDomainGroupId;
    }

    if (!isNew) {
      filteredDomainOptions = domainOptions;
    }

    const domainNameOptions = getDomainOptionsByDomainGroupId(
      editedParameterCategory.domainGroup.id,
      false,
      pdApi
    );

    if (editedParameterCategory.domain === null && domainNameOptions.length > 0) {
      editedParameterCategory.domain = {
        id: domainNameOptions[0].value,
        url: ""
      };
    } else if (editedParameterCategory.domain.id === -1 && domainNameOptions.length > 0) {
      editedParameterCategory.domain = {
        id: domainNameOptions[0].value,
        url: ""
      };
    }

    const groupName: Api.GroupName | undefined = _.values(pdApi.domainGroups).find(
      (domainGroup: Api.DomainGroup) => domainGroup.id === editedParameterCategory.domainGroup.id
    )?.enumName as Api.GroupName;
    // show deleteButton for root only if it has no subcategories
    const deleteDisabled =
      storedParameterCategory &&
      parameterCategoryHasChildren(storedParameterCategory.id, parameterCategories);

    const parameterTargets: { [key: string]: string } = {
      Lift: "liftParameters",
      APM: "apmParameters",
      Drive: "driveParameters",
      Escalator: "escalatorParameters",
      Door: "doorParameters",
      Group: "groupParameters",
      Safety: "safetyParameters",
      Monitoring: "liftParameters",
      NonKone: "liftParameters",
      Unknown: "liftParameters",
      Signalization: "liftParameters",
      Network: "liftParameters"
    };
    let usedInParameter = false;

    if (this.props.editedParameterCategory.extraDomainGroupName) {
      const key: string =
        parameterTargets[this.props.editedParameterCategory?.extraDomainGroupName];
      // eslint-disable-next-line
      const parameterList = this.props.pdApi[key as keyof State.PdApi] as any[];
      usedInParameter =
        parameterList.findIndex(
          parameter => parameter.parameterCategory?.id === this.props.editedParameterCategory.id
        ) > -1;
    }

    const userPermissions = hasWriteAccess(
      activeUser,
      groupName === "Lift" ? ["Lift", "LIO"] : groupName
    );

    const errorEnumValue = this.isFieldValid(parameterCategoryFields("enumValue"));
    const errorEnumName = this.isFieldValid(parameterCategoryFields("enumName"));
    const errorName = this.isFieldValid(parameterCategoryFields("name"));
    const errorDescription = this.isFieldValid(parameterCategoryFields("description"));
    const errorDomainGroup = this.isFieldValid(parameterCategoryFields("domainGroup"));
    const errorDomain = this.isFieldValid(parameterCategoryFields("domain"));
    return (
      <Editor
        itemName={editorTitle}
        originalObject={storedParameterCategory}
        editedObject={editedParameterCategory}
        hasWritePermission={hasWriteAccess(
          activeUser,
          groupName === "Lift" ? ["Lift", "LIO"] : groupName
        )}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => this.props.editActions.update("parameterCategories")}
        create={() => this.props.editActions.add("parameterCategories")}
        remove={{
          callback: toggleModal,
          enabledTooltip: "parameter category",
          disabledTooltip: usedInParameter
            ? "as it is linked to Parameter."
            : "as it is a root category with linked subcategories.",
          disabled: deleteDisabled || usedInParameter
        }}
      >
        {selectedParentId && selectedParentId !== editedParameterCategory.id && (
          <DropdownField
            id="parentId"
            label="Parent name"
            value={selectedParentId}
            options={[{ key: 1, text: parentCategoryName, value: selectedParentId }]}
            disabled={true} // This can only be changed by selecting different root category in UI
            onChange={() => undefined}
          />
        )}

        <NumberField
          id="code"
          label="Code"
          value={editedParameterCategory.enumValue}
          isError={!errorEnumValue.errorStatus}
          errorMessage={errorEnumValue.errorMsg}
          readonly={!isNew || !userPermissions || !accessdisable}
          onChange={enumValue =>
            this.props.editActions.setEdited({
              ...editedParameterCategory,
              enumValue
            })
          }
        />
        <TextField
          id="enumeration"
          label="Enumeration"
          value={editedParameterCategory.enumName}
          readonly={!isNew || !userPermissions || !accessdisable} // PD Api does not suppor updating this field
          isError={!errorEnumName.errorStatus}
          errorMessage={errorEnumName.errorMsg}
          onChange={enumName =>
            this.props.editActions.setEdited({
              ...editedParameterCategory,
              enumName: enumName.trim()
            })
          }
        />
        <TextField
          id="name"
          label="Name"
          value={editedParameterCategory.name}
          isError={!errorName.errorStatus}
          errorMessage={errorName.errorMsg}
          onChange={name => {
            this.props.editActions.setEdited({
              ...editedParameterCategory,
              name: name.trim()
            });
          }}
          readonly={!userPermissions}
        />
        <TextField
          id="description"
          label="Description"
          value={editedParameterCategory.description}
          isError={!errorDescription.errorStatus}
          errorMessage={errorDescription.errorMsg}
          onChange={description => {
            this.props.editActions.setEdited({
              ...editedParameterCategory,
              description: description.trim()
            });
          }}
          readonly={!userPermissions || !accessdisable}
        />

        {domainGroupId === undefined && (
          <DropdownField
            id="domainGroup"
            label="Domain Group"
            value={editedParameterCategory.domainGroup.id}
            isError={!errorDomainGroup.errorStatus}
            errorMessage={errorDomainGroup.errorMsg}
            options={filteredDomainOptions}
            disabled={
              !isNew ||
              domainGroupId !== undefined ||
              !!parentCategoryDomainGroupId ||
              filteredDomainOptions.length == 1 ||
              !accessdisable
            } // PD Api does not support updating this field
            onChange={domainGroupIndex => {
              const domainGroup = pdApi.domainGroups[domainGroupIndex];
              const domain = {
                id: -1,
                url: ""
              };
              this.props.editActions.setEdited({
                ...editedParameterCategory,
                domainGroup,
                domain
              });
            }}
          />
        )}
        <DropdownField
          id="domainName"
          label="Domain"
          value={editedParameterCategory.domain.id}
          isError={!errorDomain.errorStatus}
          errorMessage={errorDomain.errorMsg}
          options={domainNameOptions}
          onChange={domainIndex => {
            const domain = {
              id: pdApi.domains[domainIndex].id,
              url: pdApi.domains[domainIndex].url
            };
            this.props.editActions.setEdited({
              ...editedParameterCategory,
              domain
            });
          }}
          disabled={!userPermissions || !accessdisable}
        />
        {(!userPermissions || !accessdisable) && (
          <p className="color-red">Your role doesn't have neccessary rights</p>
        )}
      </Editor>
    );
  }

  private isFieldValid = (field: string): ErrorStatus => {
    const { pdApi, editedParameterCategory } = this.props;
    const restrictedIds = [1]; //Unknown
    switch (field) {
      case parameterCategoryFields("enumValue"):
        return _.values(pdApi.parameterCategories).find(
          parameterCategory =>
            parameterCategory.id !== editedParameterCategory.id &&
            parameterCategory.enumValue === editedParameterCategory.enumValue &&
            parameterCategory.domainGroup.id === editedParameterCategory.domainGroup.id
        )
          ? { errorStatus: false, errorMsg: "Code must be unique in the domain group." }
          : { errorStatus: true, errorMsg: "" };

      case parameterCategoryFields("enumName"): {
        const { enumName } = editedParameterCategory;
        if (this.isEmptyString(enumName)) return { errorStatus: false, errorMsg: "" };
        //if empty its inavalid return false
        else {
          const editedObjectParent = editedParameterCategory.parentCategory
            ? editedParameterCategory.parentCategory.id
            : null;

          let filteredParameterCategoriesRoot;
          let filteredParameterCategoriesSub;
          let filteredParameterCategories;
          //sub enumeration
          if (editedParameterCategory.parentCategory) {
            //to check if sub category name is already used as sub in the same root
            //compare among sub categories
            filteredParameterCategoriesRoot = _.values(pdApi.parameterCategories).filter(
              row => row.parentCategory && row.parentCategory.id === editedObjectParent
            );
            const root = filteredParameterCategoriesRoot.find(
              parameterCategory =>
                parameterCategory.id !== editedParameterCategory.id &&
                parameterCategory.enumName === editedParameterCategory.enumName.trim()
            );
            if (root) {
              return {
                errorStatus: false,
                errorMsg: "Sub enumeration must be unique in the root category."
              };
            }
            //to check if sub category name is already used as root in the same domain
            //compare among root categories
            filteredParameterCategoriesSub = _.values(pdApi.parameterCategories).filter(
              row =>
                row.parentCategory === null &&
                row.domainGroup.id === editedParameterCategory.domainGroup.id
            );
            const sub = filteredParameterCategoriesSub.find(
              parameterCategory =>
                parameterCategory.id !== editedParameterCategory.id &&
                parameterCategory.enumName === editedParameterCategory.enumName.trim()
            );
            if (sub) {
              return {
                errorStatus: false,
                errorMsg: "Enumeration is already used as root category in the same domain."
              };
            }
          } else {
            //root enumeration
            //to check if root category name is already used as root/sub in the same domain
            //compare among all categories
            filteredParameterCategories = _.values(pdApi.parameterCategories).filter(
              row => row.domainGroup.id === editedParameterCategory.domainGroup.id
            );
            return filteredParameterCategories.find(
              parameterCategory =>
                parameterCategory.id !== editedParameterCategory.id &&
                parameterCategory.enumName === editedParameterCategory.enumName.trim()
            )
              ? {
                  errorStatus: false,
                  errorMsg: "Root enumeration must be unique in the domain group."
                }
              : { errorStatus: true, errorMsg: "" };
          }
        }
        return { errorStatus: true, errorMsg: "" }; // if no error return true
      }
      case parameterCategoryFields("name"): {
        const { name } = editedParameterCategory;
        if (this.isEmptyString(name)) return { errorStatus: false, errorMsg: "" };
        //check empty string
        else return { errorStatus: true, errorMsg: "" }; // if no error return true;
      }
      case parameterCategoryFields("description"): {
        const { description } = editedParameterCategory;
        if (this.isEmptyString(description)) return { errorStatus: false, errorMsg: "" };
        //check empty string
        else return { errorStatus: true, errorMsg: "" }; // if no error return true;
      }
      case parameterCategoryFields("domainGroup"): {
        const { domainGroup } = editedParameterCategory;
        return restrictedIds.includes(domainGroup.id)
          ? { errorStatus: false, errorMsg: "Category cannot be created for Unknown Domain Group." }
          : { errorStatus: true, errorMsg: "" };
      }
      case parameterCategoryFields("domain"): {
        const { domainGroup } = editedParameterCategory;
        return restrictedIds.includes(domainGroup.id)
          ? { errorStatus: false, errorMsg: "" }
          : { errorStatus: true, errorMsg: "" }; // if no error return true;
      }
      default:
        return { errorStatus: true, errorMsg: "" }; // if no error default return true;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private buildEditorTitle = (isNew: boolean): string => {
    let editorTitle: string;
    if (
      !this.props.selectedParentId ||
      this.props.selectedParentId === this.props.editedParameterCategory.id
    ) {
      editorTitle = "root ";
    } else {
      editorTitle = "sub ";
    }
    editorTitle += "parameter category";
    return editorTitle;
  };

  private isEmptyString = (str: string | null): boolean =>
    !(!!(str ?? "").trim() && (str ?? "").trim().length > 0);

  private isAllFieldsValid = (): boolean => {
    const { editedParameterCategory } = this.props;
    const temp = _.keys(editedParameterCategory);
    for (const key of temp) {
      if (!this.isFieldValid(key).errorStatus) {
        return false;
      }
    }
    return true;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedParameterCategory: state.editView.editedObject,
  storedParameterCategory: state.editView.storedObject,
  pdApi: state.pdApi,
  activeUser: state.main.activeUser
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditParameterCategory);
